import { FileDownload } from '@/assets/icons'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { MedChronJobActions } from '../../store/slice'
import { MedChronJobStatus } from '../../store/schemas'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { trackMedChronEvent } from '@/analytics/utils/medchron-analytics'

interface MedChronJobDownloadButtonProps {
  jobId: string
  jobStatus: MedChronJobStatus
  jobName: string
  compact?: boolean
}

/**
 * Medical Chronology Job Download Button
 *
 * A button to download the latest artifact version of the medical chronology job.
 */
export default function MedChronJobDownloadButton(props: MedChronJobDownloadButtonProps) {
  const { jobId, jobStatus, jobName, compact = false } = props
  const dispatch = useAppDispatch()
  const latestArtifactVersionLoading = useAppSelector((state: RootState) => state.medChronJobState.latestArtifactVersionLoading)

  const isLoading = latestArtifactVersionLoading[jobId]
  const isDone = jobStatus === MedChronJobStatus.DONE

  const ariaLabel = `Download ${jobName}`

  // Only show button if the job is completed
  if (!isDone) {
    return null
  }

  const handleDownload = () => {
    trackMedChronEvent(AnalyticsEvent.MedChronDownloadClicked, { medChronJobId: jobId })
    dispatch(MedChronJobActions.getLatestArtifactVersion(jobId))
  }

  // Compact version rendered as a plain icon button
  if (compact) {
    return (
      <button onClick={handleDownload} disabled={isLoading} className="rounded-full p-1 hover:bg-brand-100" aria-label={ariaLabel}>
        <FileDownload className="w-6 h-6 text-brand-500" />
      </button>
    )
  }

  return (
    <div className="inline-flex items-center justify-center w-7 h-7">
      {isLoading ? (
        <CircularProgressContinuousSized size={14} thickness={4} />
      ) : (
        <button
          onClick={handleDownload}
          disabled={isLoading}
          className="w-7 h-7 flex items-center justify-center rounded-md ring-1 ring-brand-500 transition-colors hover:bg-brand-neutral-200"
          aria-label={ariaLabel}
        >
          <FileDownload className="w-4 h-4 text-brand-500" />
        </button>
      )}
    </div>
  )
}
